import { CommunicationApi } from '@app/api';
import { Communication } from '@app/model/Communication';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { create } from 'zustand';

/**
 * The communications the service can send, held once per session.
 *
 * Not a createEntityStore, for the reason parameterStore is not one either: that helper is built
 * on `id`, and a communication has none — the list is the service's *catalog*, so one nobody has
 * overridden has no row to have an id. It is addressed by its constant name instead.
 *
 * Leaner than parameterStore in one deliberate way: there is no staleness clock. Nothing outside
 * the administration screen reads a communication, so there is no hot path holding a cached value
 * that could quietly go out of date — the screen forces a read on mount, and the entity websocket
 * pushes every save to every connected client, which EntityUpdateStream turns into a reload here.
 * A timer on top of that would only add a request nobody is waiting for.
 */

interface CommunicationStore {
  items: Communication[];
  /** Constant name to communication. The lookup a single-row update uses. */
  byName: Record<string, Communication>;
  isLoaded: boolean;
  /** Why the last load failed, or null. Parked rather than thrown — see load(). */
  error: ApiError | null;

  /**
   * Fills the cache. Records failures on `error` instead of rejecting, so a view may call it
   * fire-and-forget, and collapses concurrent calls into the one request.
   *
   * @param forceRefresh ignores the cache. What the admin screen uses on mount: that page exists
   *     to show what the service is using right now.
   */
  load: (forceRefresh?: boolean) => Promise<void>;

  /** Replaces one communication from what a write returned, so the writer's own tab is instantly
   * right without waiting for the socket to come back around. */
  applyWrite: (communication: Communication) => void;
}

const indexByName = (items: Communication[]): Record<string, Communication> =>
  items.reduce<Record<string, Communication>>((index, communication) => {
    index[communication.name] = communication;
    return index;
  }, {});

// Outside the store: an in-flight request is not state anything renders, and keeping it here is
// what makes two components mounting at once share one request rather than race.
let inFlight: Promise<void> | null = null;

export const useCommunicationStore = create<CommunicationStore>((set, get) => ({
  items: [],
  byName: {},
  isLoaded: false,
  error: null,

  load: async (forceRefresh = false) => {
    if (!forceRefresh && get().isLoaded) return;

    // A forced refresh still joins a request already on the wire: it was issued no earlier than
    // this call, so its answer is just as current as a second request's would be.
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        const items = await CommunicationApi.getAll();
        set({ items, byName: indexByName(items), isLoaded: true, error: null });
      } catch (err) {
        // Left non-fatal on purpose, as parameterStore does: the screen renders the error itself,
        // and a failure to read the catalog must not take a page down.
        const error = toApiError(err);
        set({ error });
        console.warn(`[Communications] Could not load the communications: ${error.message}`);
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  },

  applyWrite: (communication) => {
    const items = get().items.map(c => (c.name === communication.name ? communication : c));
    set({ items, byName: indexByName(items) });
  }
}));
